/**
 * Home on the console (AIOE 0.65.0): the workbench's Home — the week's strip, the numbers and the feed — built by
 * the platform for the signed-in person across their organisation, from what the platform holds (approvals,
 * questions and the rest of their inbox, plans waiting and plans being wrapped up, runs, pull requests, nodes,
 * update orders). The shapes are the workbench's `shared/schemas/home.ts` (`homeBundleSchema`), built by the
 * workbench's own models (`@aioe/shared/models`, mirrored from its `shared/home/`), so a card here says what it
 * says on the desktop. AIOE's own contract.
 *
 *   GET  /home/v1            a person (a sign-in): {@link HomeResponse}; an ETag, and 304 for an unchanged answer
 *   POST /home/v1/feedback   a person: {@link HomeFeedback} -> {@link HomeFeedbackAnswer}; kept per person, so every
 *                            browser they use agrees
 *
 * What is different from the desktop's bundle, on purpose:
 *   - a card's `open.view` is a console path (it starts with `/`); `open.phone` still names the phone's view;
 *   - the Plan usage number is left out (the platform does not see a person's plan windows) and named in
 *     `notComputed`, with the other inputs only a desktop has;
 *   - every card belongs to the organisation's side (`home: "organisation"`), so teammates' runs and nodes appear.
 *
 * Feedback: `useful` and `not-useful` lean the card's whole kind (pressing the one already on clears it, as the
 * desktop's toggle); `pin` / `unpin` put the card first or take it back; `hide` hides it for a week unless its record
 * changes (`changeKey`, which the answer's card carries); `show` brings a hidden card back. The card's kind is the
 * part of its id before the first colon.
 */
import { z } from "zod";
import { homeBundleSchema, homeFeedKindSchema } from "./home.js";
export const HOME_PATH = "/home/v1";
export const HOME_FEEDBACK_PATH = "/home/v1/feedback";
export const HOME_FEEDBACK_ACTIONS = ["useful", "not-useful", "pin", "unpin", "hide", "show"];
export const homeFeedbackActionSchema = z.enum(HOME_FEEDBACK_ACTIONS);
export const homeFeedbackSchema = z.object({
    /** A feed card's `id` (`kind:record`). */
    cardId: z.string().min(3).max(300),
    action: homeFeedbackActionSchema,
    /** The card's `changeKey`: what `hide` keeps, so a change brings the card back. Required for `hide`. */
    changeKey: z.string().max(300).optional(),
});
/** What a person taught their feed (the workbench's `FeedPrefs`, less the desktop's dock and favourites). */
export const homeFeedPrefsSchema = z.object({
    weights: z.record(z.string().max(40), z.number().int().min(-3).max(3)),
    /** Pinned card ids, the first pin first. */
    pinned: z.array(z.string().max(300)).max(200),
    /** Hidden card ids: until when, and what the card looked like then. */
    hidden: z.record(z.string().max(300), z.object({ until: z.number(), changeKey: z.string().max(300) })),
});
export const homeResponseSchema = homeBundleSchema.extend({
    /** ISO time it was built. */
    at: z.string().max(40),
    /** `organisation` for an administrator, an operator or a portfolio manager; `mine` otherwise (their projects and machines). */
    scope: z.enum(["organisation", "mine"]),
    /** The person's own feed preferences, so a surface can show which way a kind leans and order the feed again after a press. */
    prefs: homeFeedPrefsSchema,
    /** The inputs the platform cannot see and leaves out rather than guess, in words. */
    notComputed: z.array(z.string().max(120)).max(12),
    /** The two chips at the end of the strip. */
    chips: z.object({ needsYou: z.number().int().nonnegative(), routinesToday: z.number().int().nonnegative() }),
    /**
     * What the strip was drawn from (the workbench's `DayEvent`s): `strip` is cut at the platform's midnights, so a
     * surface in another time zone draws the strip again from these with the same model (`weekAhead`).
     */
    events: z.array(z.object({ kind: z.enum(["routine", "plan", "release", "review"]), at: z.number(), label: z.string().max(200), owner: z.string().max(120).optional() })).max(200),
});
/** A card's kind from its id: the part before the first colon, when it is a kind the feed has. */
export function homeCardKind(cardId) {
    const at = cardId.indexOf(":");
    return at > 0 ? homeFeedKindSchema.safeParse(cardId.slice(0, at)).data : undefined;
}
//# sourceMappingURL=homeRoute.js.map